/**
 * The ring, made rather than played.
 *
 * Generated with WebAudio so the app carries no sound files (the published
 * demo inlines every asset, and a ring is the one sound that must work the
 * first time). Seven tones, each a short phrase repeated on its own period —
 * enough to be heard across a control room, not an alarm. The ids are shared
 * with the phone app (calls-contract-v2 §1), so a person recognises their
 * tone on either: classic (the first tone, and the default), chime, marimba,
 * pulse, digital, soft and terminal. `chime` is also the quieter sound of a
 * meeting reminder.
 *
 * The choice is kept in this browser: `sky.calls.ringtone` = {tone, volume},
 * read and written through try/catch.
 *
 * Browsers start an AudioContext suspended until the page has been touched.
 * A ring that arrives before any click may therefore be silent; the ring
 * card, the title and the notification still carry it, which is why there
 * are four signals and not one.
 */

export type ToneId = "classic" | "chime" | "marimba" | "pulse" | "digital" | "soft" | "terminal";

interface NoteSpec {
  /** Hz */
  f: number;
  /** seconds after the phrase starts */
  at: number;
  /** seconds */
  len: number;
  wave?: OscillatorType;
  /** 0..1 before the volume */
  gain?: number;
}

interface Tone {
  id: ToneId;
  label: string;
  /** a few words on how it sounds, for the picker */
  hint: string;
  /** ms between phrases while ringing */
  period: number;
  notes: NoteSpec[];
}

const twice = (ns: NoteSpec[], gap: number): NoteSpec[] => [...ns, ...ns.map((n) => ({ ...n, at: n.at + gap }))];

export const TONES: readonly Tone[] = [
  {
    id: "classic",
    label: "Classic",
    hint: "Two notes, twice — the first tone",
    period: 3000,
    /* E6 then B5, twice: rises and falls the way a phone does */
    notes: twice(
      [
        { f: 1318.5, at: 0, len: 0.35 },
        { f: 987.8, at: 0.18, len: 0.45 },
      ],
      0.7,
    ),
  },
  {
    id: "chime",
    label: "Chime",
    hint: "A bell, gently",
    period: 3600,
    notes: [
      { f: 1046.5, at: 0, len: 1.1, wave: "triangle", gain: 1.2 },
      { f: 784, at: 0.42, len: 1.4, wave: "triangle", gain: 1.2 },
    ],
  },
  {
    id: "marimba",
    label: "Marimba",
    hint: "A quick wooden run",
    period: 2600,
    notes: [523.3, 659.3, 784, 1046.5, 784, 1046.5].map((f, i) => ({ f, at: i * 0.13, len: 0.22, gain: 1.3 })),
  },
  {
    id: "pulse",
    label: "Pulse",
    hint: "Four short beeps",
    period: 2400,
    notes: [0, 0.2, 0.4, 0.6].map((at) => ({ f: 880, at, len: 0.12, gain: 0.9 })),
  },
  {
    id: "digital",
    label: "Digital",
    hint: "An old desk phone's trill",
    period: 3000,
    notes: Array.from({ length: 12 }, (_, i) => ({
      f: i % 2 ? 1400 : 1000,
      at: i * 0.06 + (i >= 6 ? 0.3 : 0),
      len: 0.06,
      wave: "square" as OscillatorType,
      gain: 0.32,
    })),
  },
  {
    id: "soft",
    label: "Soft",
    hint: "Low and slow, for a quiet office",
    period: 4000,
    notes: [
      { f: 523.3, at: 0, len: 0.9, gain: 0.8 },
      { f: 659.3, at: 0.5, len: 1.1, gain: 0.8 },
    ],
  },
  {
    id: "terminal",
    label: "Terminal",
    hint: "Three notes, like an airport announcement",
    period: 4200,
    notes: [
      { f: 784, at: 0, len: 0.7, wave: "triangle", gain: 1.3 },
      { f: 987.8, at: 0.38, len: 0.7, wave: "triangle", gain: 1.3 },
      { f: 1174.7, at: 0.76, len: 1.1, wave: "triangle", gain: 1.3 },
    ],
  },
];

export const DEFAULT_TONE: ToneId = "classic";
export const DEFAULT_VOLUME = 80;
/** the reminder before a meeting: the chime, quieter than a ring */
export const REMINDER_TONE: ToneId = "chime";

export function toneById(id: string | undefined): Tone {
  return TONES.find((t) => t.id === id) ?? TONES[0];
}

/* ── the setting ──────────────────────────────────────────────────────── */

export interface RingSetting {
  tone: ToneId;
  /** 0..100 */
  volume: number;
}

export const RINGTONE_KEY = "sky.calls.ringtone";

export function readRingSetting(): RingSetting {
  try {
    const raw = localStorage.getItem(RINGTONE_KEY);
    const v = raw ? (JSON.parse(raw) as Partial<RingSetting>) : {};
    const volume =
      typeof v.volume === "number" && v.volume >= 0 && v.volume <= 100 ? Math.round(v.volume) : DEFAULT_VOLUME;
    return { tone: toneById(v.tone).id, volume };
  } catch {
    return { tone: DEFAULT_TONE, volume: DEFAULT_VOLUME };
  }
}

export function writeRingSetting(s: RingSetting): void {
  try {
    localStorage.setItem(RINGTONE_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable: the choice lasts as long as the page */
  }
}

/* ── the sound ────────────────────────────────────────────────────────── */

type Ctx = AudioContext;

let ctx: Ctx | null = null;

function context(): Ctx | null {
  if (ctx) return ctx;
  const C =
    (globalThis as { AudioContext?: typeof AudioContext }).AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!C) return null;
  try {
    ctx = new C();
  } catch {
    return null;
  }
  return ctx;
}

/** 0.18 at full volume — the level the first tone always rang at. */
const PEAK = 0.18;

function note(c: Ctx, out: AudioNode, n: NoteSpec, start: number, volume: number) {
  const peak = Math.max(0.0002, PEAK * (n.gain ?? 1) * (volume / 100));
  const osc = c.createOscillator();
  const gain = c.createGain();
  const at = start + n.at;
  osc.type = n.wave ?? "sine";
  osc.frequency.value = n.f;
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(peak, at + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + n.len);
  osc.connect(gain).connect(out);
  osc.start(at);
  osc.stop(at + n.len + 0.05);
}

function phrase(c: Ctx, tone: Tone, volume: number) {
  if (volume <= 0) return;
  const start = c.currentTime + 0.05;
  for (const n of tone.notes) note(c, c.destination, n, start, volume);
}

/** Start ringing; the returned function stops it. Silent where there is no WebAudio. */
export function startRing(toneId: ToneId = DEFAULT_TONE, volume = DEFAULT_VOLUME): () => void {
  const c = context();
  if (!c) return () => undefined;
  const tone = toneById(toneId);
  void c.resume?.().catch(() => undefined);
  phrase(c, tone, volume);
  const timer = setInterval(() => phrase(c, tone, volume), tone.period);
  return () => clearInterval(timer);
}

/** One phrase of a tone: the picker's Preview, and the meeting reminder. */
export function playOnce(toneId: ToneId, volume = DEFAULT_VOLUME): void {
  const c = context();
  if (!c) return;
  void c.resume?.().catch(() => undefined);
  phrase(c, toneById(toneId), volume);
}
